export function moveLeft(line: number[]): number[] {
    // Step 1: Filter out zeros (slide all tiles to the left)
    const filtered = line.filter(cell => cell !== 0);

    // Step 2: Merge adjacent tiles with the same value
    const merged: number[] = [];
    let i = 0;
    while (i < filtered.length) {
        // If current tile equals next tile, merge them
        if (i < filtered.length - 1 && filtered[i] === filtered[i + 1]) {
            merged.push(filtered[i] * 2);
            i += 2; // Skip the next tile since we merged it
        } else {
            merged.push(filtered[i]);
            i += 1;
        }
    }

    // Step 3: Fill the rest with zeros
    while (merged.length < line.length) {
        merged.push(0);
    }

    return merged;
}

// Helper function: Reverse a line
function reverseLine(line: number[]): number[] {
    return [...line].reverse();
}

// Helper function: Transpose matrix (swap rows and columns)
function transposeMatrix(matrix: number[][]): number[][] {
    return matrix[0].map((_, colIndex) => matrix.map(row => row[colIndex]));
}

// Function to apply moveLeft to entire matrix
export function moveMatrixLeft(matrix: number[][]): number[][] {
    return matrix.map(row => moveLeft(row));
}

// Function to move right: reverse each row, move left, then reverse back
export function moveMatrixRight(matrix: number[][]): number[][] {
    return matrix.map(row => reverseLine(moveLeft(reverseLine(row))));
}

// Function to move up: transpose, move left, transpose back
export function moveMatrixUp(matrix: number[][]): number[][] {
    const transposed = transposeMatrix(matrix);
    const moved = moveMatrixLeft(transposed);
    return transposeMatrix(moved);
}

// Function to move down: transpose, move right, transpose back
export function moveMatrixDown(matrix: number[][]): number[][] {
    const transposed = transposeMatrix(matrix);
    const moved = moveMatrixRight(transposed);
    return transposeMatrix(moved);
}

// Helper function: Check if two matrices are equal
function matricesEqual(matrix1: number[][], matrix2: number[][]): boolean {
    if (matrix1.length !== matrix2.length) {
        return false;
    }
    for (let i = 0; i < matrix1.length; i++) {
        if (matrix1[i].length !== matrix2[i].length) {
            return false;
        }
        for (let j = 0; j < matrix1[i].length; j++) {
            if (matrix1[i][j] !== matrix2[i][j]) {
                return false;
            }
        }
    }
    return true;
}

// Function to check if there are any empty cells
export function hasEmptyCells(matrix: number[][]): boolean {
    for (const row of matrix) {
        for (const cell of row) {
            if (cell === 0) {
                return true;
            }
        }
    }
    return false;
}

// Function to check if any moves are possible
export function canMove(matrix: number[][]): boolean {
    // Check if any move would change the matrix
    if (!matricesEqual(matrix, moveMatrixLeft(matrix))) {
        return true;
    }
    if (!matricesEqual(matrix, moveMatrixRight(matrix))) {
        return true;
    }
    if (!matricesEqual(matrix, moveMatrixUp(matrix))) {
        return true;
    }
    if (!matricesEqual(matrix, moveMatrixDown(matrix))) {
        return true;
    }
    return false;
}

// Function to check if the game is over
export function isGameOver(matrix: number[][]): boolean {
    return !hasEmptyCells(matrix) && !canMove(matrix);
}

// Function to add a random 2 tile to an empty cell
export function addRandomTile(matrix: number[][]): number[][] {
    const emptyCells: {row: number, col: number}[] = [];

    // Find all empty cells
    for (let i = 0; i < matrix.length; i++) {
        for (let j = 0; j < matrix[i].length; j++) {
            if (matrix[i][j] === 0) {
                emptyCells.push({row: i, col: j});
            }
        }
    }

    // If no empty cells, return the matrix unchanged
    if (emptyCells.length === 0) {
        return matrix;
    }

    // Select a random empty cell
    const randomIndex = Math.floor(Math.random() * emptyCells.length);
    const {row, col} = emptyCells[randomIndex];

    // Create a new matrix with the new tile (90% chance for 2, 10% chance for 4)
    const newMatrix = matrix.map(r => [...r]);
    newMatrix[row][col] = Math.random() < 0.9 ? 2 : 4;

    return newMatrix;
}